// ImportDialog.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import '../../pages/BankMatching/BankMatching.css'; // Custom animations

const ImportDialog = ({
    open,
    onClose,
    onImport,
    onPreview,
    showXtramTemplateOption = false,
    showIsracardTemplateOption = false,
}) => {
    const [userFile, setUserFile] = useState(null);
    const [useXtramTemplate, setUseXtramTemplate] = useState(false);
    const [useIsracardTemplate, setUseIsracardTemplate] = useState(false);

    const onDrop = (acceptedFiles) => {
        // Set the first file in the array as the uploaded file
        setUserFile(acceptedFiles[0]);
    };

    const handleClearFile = () => {
        setUserFile(null); // Clear the selected file
    };

    const handleClose = () => {
        setUserFile(null); // Reset the file on dialog close
        setUseXtramTemplate(false);
        setUseIsracardTemplate(false);
        onClose(); // Close the dialog
    };

    const handleImport = () => {
        if (userFile) {
            // Call preview first instead of direct import
            if (onPreview) {
                onPreview(userFile, useXtramTemplate, useIsracardTemplate);
            } else {
                onImport(userFile, useXtramTemplate, useIsracardTemplate); // Fallback to direct import if no preview callback
            }
            handleClose(); // Close the dialog
        }
    };

    const { t } = useTranslation();
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle style={{ textAlign: 'right' }}>{t("ImportFile")}</DialogTitle>
            <DialogContent>
                <div
                    {...getRootProps()}
                    style={{
                        border: '2px dashed #ccc',
                        borderRadius: '5px',
                        padding: '40px',
                        textAlign: 'center',
                        backgroundColor: isDragActive ? '#e0f7fa' : '#fafafa',
                        transition: 'background-color 0.3s ease',
                        cursor: 'pointer',
                    }}
                >
                    <input {...getInputProps()} />
                    {userFile ? (
                        <div>
                            {/* Display file name and file icon if a file is uploaded */}
                            <Typography variant="h6" style={{ color: "#7d7d7d", marginBottom: '10px' }}>
                                {userFile.name} {/* Display the name of the uploaded file */}
                            </Typography>
                            <InsertDriveFileIcon style={{ fontSize: '50px', color: '#7d7d7d' }} />
                        </div>
                    ) : isDragActive ? (
                        <div>
                            <Typography variant="h6" style={{ color: "#7d7d7d" }}>{t("DropFilesHere")}</Typography>
                            <CloudUploadIcon className="animated-upload-icon" />
                        </div>
                    ) : (
                        <div>
                            <Typography variant="h6" style={{ color: "#7d7d7d" }}>
                                {t("DragDropFilesOrClick")}
                            </Typography>
                            <CloudUploadIcon className="animated-upload-icon" />
                        </div>
                    )}
                </div>

                {/* Add Clear button when file is uploaded */}
                {userFile && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                        <Button
                            variant="outlined"
                            onClick={handleClearFile}
                            color="secondary"
                        >
                            {t("Clear")}
                        </Button>
                    </div>
                )}

                {showXtramTemplateOption && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useXtramTemplate}
                                    onChange={(e) => {
                                        const v = e.target.checked;
                                        setUseXtramTemplate(v);
                                        if (v) setUseIsracardTemplate(false);
                                    }}
                                    sx={{
                                        color: '#304FFF',
                                        '&.Mui-checked': { color: '#304FFF' },
                                    }}
                                />
                            }
                            label={t("UseXtramTemplate")}
                        />
                    </div>
                )}
                {showIsracardTemplateOption && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useIsracardTemplate}
                                    onChange={(e) => {
                                        const v = e.target.checked;
                                        setUseIsracardTemplate(v);
                                        if (v) setUseXtramTemplate(false);
                                    }}
                                    sx={{
                                        color: '#304FFF',
                                        '&.Mui-checked': { color: '#304FFF' },
                                    }}
                                />
                            }
                            label={t("UseIsracardTemplate")}
                        />
                    </div>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button onClick={handleClose}>{t("Cancel")}</Button>
                <Button
                    onClick={handleImport}
                    variant="contained"
                    color="primary"
                    disabled={!userFile} // Disable if no file is uploaded
                >
                    {t("Import")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImportDialog;
